import { Helmet } from 'react-helmet-async';

const PrivacyPolicy = () => {
  const updated = 'July 2026';

  return (
    <>
      <Helmet>
        <title>Privacy Policy — ChooseMyCollege</title>
        <meta name="description" content="Privacy Policy for ChooseMyCollege — how we collect, use and protect your data." />
        <link rel="canonical" href="https://choosemycollege.in/privacy-policy" />
      </Helmet>

      <div className="max-w-3xl mx-auto py-6">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-10 space-y-7">

          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">Privacy Policy</h1>
            <p className="text-sm text-gray-400">Last updated: {updated}</p>
          </div>

          <p className="text-gray-600 text-sm leading-relaxed">
            This Privacy Policy explains how <strong>ChooseMyCollege</strong> ("we", "us", "our"), accessible at{' '}
            <strong>choosemycollege.in</strong>, collects, uses, and protects information when you use our website.
            By using the site you agree to this policy.
          </p>

          <Section title="1. Information We Collect">
            <p>We collect information in two ways:</p>
            <ul>
              <li>
                <strong>Information you provide</strong> — When you use the free counselling enquiry form we collect
                your name, mobile number, TNEA cutoff mark or rank, and community. This is sent directly via WhatsApp
                and is not stored on our servers.
              </li>
              <li>
                <strong>Automatically collected data</strong> — Standard web server logs, browser type, pages visited,
                and time spent. This data is anonymous and used only to improve the site.
              </li>
            </ul>
          </Section>

          <Section title="2. How We Use Your Information">
            <ul>
              <li>To respond to your counselling enquiry via WhatsApp</li>
              <li>To improve website content and features</li>
              <li>To understand which pages and tools are most useful</li>
            </ul>
            <p className="mt-3">
              We do <strong>not</strong> sell, trade, or rent your personal information to third parties.
              We do not send promotional SMS or calls unless you explicitly request it.
            </p>
          </Section>

          <Section title="3. Cookies">
            <p>
              We use cookies to improve your experience. Cookies are small text files stored on your device.
              We use:
            </p>
            <ul>
              <li><strong>Essential cookies</strong> — required for the site to function (e.g. remembering your filter selections)</li>
              <li><strong>Analytics cookies</strong> — anonymous data via Google Analytics to understand traffic patterns</li>
              <li><strong>Advertising cookies</strong> — if Google AdSense ads are displayed, Google may use cookies to show relevant ads based on your browsing history</li>
            </ul>
            <p className="mt-3">
              You can disable cookies in your browser settings. Disabling cookies may affect some features.
            </p>
          </Section>

          <Section title="4. Google AdSense & Third-Party Advertising">
            <p>
              We may display advertisements served by Google AdSense. Google uses cookies to serve ads based on your
              prior visits to this and other websites. You can opt out of personalised advertising by visiting{' '}
              <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer"
                className="text-blue-600 hover:underline">
                google.com/settings/ads
              </a>.
            </p>
            <p className="mt-3">
              Third-party vendors, including Google, use cookies to serve ads based on a user's prior visits to our
              website or other websites. Google's use of advertising cookies enables it and its partners to serve ads
              to users based on their visit to our site and/or other sites on the Internet.
            </p>
            <p className="mt-3">
              Specifically, Google uses the <strong>DoubleClick DART cookie</strong> to serve ads to our visitors
              based on their visit to this site and other sites on the Internet. Users may opt out of the use of the
              DART cookie by visiting the{' '}
              <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer"
                className="text-blue-600 hover:underline">
                Google Ads Settings
              </a>{' '}
              page. Third-party ad vendors and networks may also use their own cookies, web beacons, or similar
              technologies to collect information about your visits to this and other websites for the purpose of
              providing advertisements about goods and services of interest to you.
            </p>
          </Section>

          <Section title="5. Google Analytics">
            <p>
              We use Google Analytics to analyse website traffic. Google Analytics collects anonymous data such as
              pages viewed, time on site, and browser type. This data does not identify you personally. For more
              information see{' '}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer"
                className="text-blue-600 hover:underline">
                Google's Privacy Policy
              </a>.
            </p>
          </Section>

          <Section title="6. WhatsApp Enquiries">
            <p>
              When you submit an enquiry form on a college page, you are redirected to WhatsApp with a pre-filled
              message. The message contains your name, score, and community. This data is transmitted via WhatsApp
              and is subject to{' '}
              <a href="https://www.whatsapp.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer"
                className="text-blue-600 hover:underline">
                WhatsApp's Privacy Policy
              </a>. We do not store this data on our servers.
            </p>
          </Section>

          <Section title="7. Data Security">
            <p>
              We implement reasonable technical measures to protect the site. However, no method of transmission
              over the internet is 100% secure. We do not store sensitive personal data such as passwords,
              financial information, or Aadhaar numbers.
            </p>
          </Section>

          <Section title="8. Children's Privacy">
            <p>
              Our service is intended for students aged 17 and above (Class 12 and above). We do not knowingly
              collect data from children under 13. If you believe a child under 13 has provided us information,
              please contact us and we will delete it.
            </p>
          </Section>

          <Section title="9. Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time. Changes will be posted on this page with an
              updated date. Continued use of the site after changes constitutes acceptance of the new policy.
            </p>
          </Section>

          <Section title="10. Contact Us">
            <p>
              If you have any questions about this Privacy Policy, please contact us:
            </p>
            <div className="mt-3 bg-gray-50 rounded-xl p-4 text-sm text-gray-700 space-y-1">
              <p><strong>ChooseMyCollege</strong></p>
              <p>Tamil Nadu, India</p>
              <p>
                WhatsApp:{' '}
                <a href="https://wa.me/918122552867" target="_blank" rel="noopener noreferrer"
                  className="text-blue-600 hover:underline">
                  +91 81225 52867
                </a>
              </p>
            </div>
          </Section>

        </div>
      </div>
    </>
  );
};

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div>
    <h2 className="text-base font-bold text-gray-800 mb-3">{title}</h2>
    <div className="text-sm text-gray-600 leading-relaxed space-y-2 [&_ul]:list-disc [&_ul]:ml-5 [&_ul]:space-y-1.5">
      {children}
    </div>
  </div>
);

export default PrivacyPolicy;
